import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { adminApi } from '../../../api/adminApi';
import { useAuth } from '../../../context/AuthContext';
import TimesheetExportModal from './TimesheetExportModal';
import TimesheetForm from './TimesheetForm';
import './TimesheetAdmin.css';

export default function TimesheetAdmin({ onBack, myPermissions, isEmbedded = false }) {
    const { user } = useAuth();
    const location = useLocation();
    // --- STATES ---
    const [mainTab, setMainTab] = useState('management');
    const [view, setView] = useState('list');

    const [employees, setEmployees] = useState([]);
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [entries, setEntries] = useState([]);
    const [settings, setSettings] = useState({ approval_mode: 'always', allow_edit_past_days: 30 });
    const [loading, setLoading] = useState(false);

    // Helper States
    const [expandedDates, setExpandedDates] = useState({});

    // EDIT/CREATE STATES
    const [editingId, setEditingId] = useState(null);
    const [isCreatingForUser, setIsCreatingForUser] = useState(false);
    const [initialFormData, setInitialFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        project_id: null,
        tasks: [{ description: '', hours: '' }],
        notes: ''
    });

    const [isExportOpen, setIsExportOpen] = useState(false);
    const [filters, setFilters] = useState({
        start: '',
        end: '',
        statuses: ['eingereicht', 'genehmigt', 'überarbeitung_angefordert'],
        sortOrder: 'desc'
    });
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const statusDropdownRef = useRef(null);
    const [showExistsModal, setShowExistsModal] = useState(false);
    const [existingEntryInfo, setExistingEntryInfo] = useState(null);

    // Suchfeld
    const [searchTerm, setSearchTerm] = useState('');

    // --- HELPER FUNCTIONS ---
    const getLocalDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
    };

    const getEntryHeaderStatus = (entry) => {
        // 1. ROT: Löschantrag (HÖCHSTE PRIORITÄT)
        if (entry.deletion_request_pending) {
            return { text: '⚠️ LÖSCHANTRAG', color: '#dc2626', bg: '#fef2f2' };
        }

        // 2. ORANGE: Eingereicht (HANDLUNGSBEDARF)
        if (entry.status === 'eingereicht') {
            const created = new Date(entry.created_at).getTime();
            const updated = new Date(entry.updated_at).getTime();
            // Wenn Update mehr als 1 Minute nach Erstellung war -> Änderung
            const isEdit = (updated - created > 60000);
            const label = isEdit ? "ÄNDERUNG" : "NEU";

            // Bernstein/Orange (#d97706 Text, #ffedd5 BG)
            return { text: `🟠 ${label}`, color: '#d97706', bg: '#ffedd5' };
        }

        // 3. GRÜN: Genehmigt (ERLEDIGT)
        if (entry.status === 'genehmigt') {
            return { text: '✅ GENEHMIGT', color: '#059669', bg: '#ecfdf5' };
        }

        // 4. GRAU: Zurückgewiesen (WARTEN AUF MITARBEITER)
        if (entry.status === 'überarbeitung_angefordert') {
            return { text: '↩️ WARTET AUF KORREKTUR', color: '#4b5563', bg: '#f3f4f6' };
        }

        // Fallback (Entwurf etc.)
        return { text: '⚪ ENTWURF', color: '#9ca3af', bg: '#f9fafb' };
    };

    // --- DATEN LADEN ---
    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const data = await adminApi.getTimesheetUsersSummary();
            setEmployees(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const fetchEmpEntries = async (userId) => {
        setLoading(true);
        try {
            const data = await adminApi.getTimesheetEntries({
                userId: userId, // Standardisiert auf userId
                startDate: filters.start,
                endDate: filters.end,
                status: filters.statuses.join(',')
            });
            // Keine redundante Client-Filterung (Backend macht das bereits)
            setEntries(data);
        } catch (err) {
            console.error(err);
            alert("Fehler beim Laden der Einträge");
        }
        finally { setLoading(false); }
    };

    const fetchSettings = async () => {
        try {
            const data = await adminApi.getTimesheetSettings();
            if (data) setSettings(data);
        } catch (err) { console.error("Settings load error", err); }
    };

    useEffect(() => {
        if (mainTab === 'management') fetchEmployees();
        if (mainTab === 'settings') fetchSettings();
    }, [mainTab]);

    useEffect(() => {
        if (selectedEmp) fetchEmpEntries(selectedEmp.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, selectedEmp]);

    useEffect(() => {
        if (location.state?.focusUserId && employees.length > 0) {
            const emp = employees.find(e => String(e.id) === String(location.state.focusUserId));
            if (emp) {
                setSelectedEmp(emp);
                setView('detail');
                if (location.state?.focusDate) {
                    const localDate = getLocalDate(location.state.focusDate);
                    setExpandedDates(prev => ({ ...prev, [localDate]: true }));
                }
            }
        }
    }, [location.state, employees]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getStatusButtonText = () => {
        if (filters.statuses.length === 0) return 'Status: Keine';
        if (filters.statuses.length === 3) return 'Status: Alle';
        return `Status: ${filters.statuses.length} gewählt`;
    };

    const toggleAllStatuses = () => {
        if (filters.statuses.length === 3) {
            setFilters(prev => ({ ...prev, statuses: [] }));
        } else {
            setFilters(prev => ({ ...prev, statuses: ['eingereicht', 'genehmigt', 'überarbeitung_angefordert'] }));
        }
    };

    const toggleStatus = (status) => {
        const newStatuses = filters.statuses.includes(status)
            ? filters.statuses.filter(s => s !== status)
            : [...filters.statuses, status];
        setFilters(prev => ({ ...prev, statuses: newStatuses }));
    };

    const resetFilters = () => {
        setFilters({
            start: '',
            end: '',
            statuses: ['eingereicht', 'genehmigt', 'überarbeitung_angefordert'],
            sortOrder: 'desc'
        });
    };

    const isFilterActive = filters.start !== '' || filters.end !== '' || filters.statuses.length !== 3 || filters.sortOrder !== 'desc';

    // --- ACTIONS (Genehmigen / Ablehnen / Löschen) ---
    const handleApprove = async (id) => {
        try {
            await adminApi.approveTimesheetEntry(id);
            setEntries(entries.map(e => e.id === id ? { ...e, status: 'genehmigt' } : e));
            fetchEmployees(); // Zähler aktualisieren
        } catch (err) { alert("Fehler beim Genehmigen"); }
    };

    const handleRejectEntry = async (id) => {
        const note = prompt("Grund für Ablehnung (Notiz):");
        if (note === null) return;
        try {
            await adminApi.rejectTimesheetEntry(id, note);
            setEntries(entries.map(e => e.id === id ? { ...e, status: 'überarbeitung_angefordert', notes: note } : e));
            fetchEmployees();
        } catch (err) { alert("Fehler beim Ablehnen"); }
    };

    const handleRejectDeletion = async (id) => {
        const reason = prompt("Grund für Ablehnung der Löschung:");
        if (reason === null) return;
        try {
            await adminApi.rejectTimesheetDeletionRequest(id, reason);
            setEntries(entries.map(e => e.id === id ? { ...e, deletion_request_pending: false } : e));
            fetchEmployees();
        } catch (err) { alert("Fehler"); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Eintrag endgültig löschen?")) return;
        try {
            await adminApi.deleteTimesheetEntry(id);
            setEntries(entries.filter(e => e.id !== id));
            fetchEmployees();
        } catch (err) { alert("Fehler beim Löschen"); }
    };

    // --- BEARBEITEN / ERSTELLEN LOGIK (MIT TIMESHEETFORM) ---

    // Rechteprüfung
    const isAdminOrManager = user?.role === 'admin' || (myPermissions && myPermissions.role === 'manager');
    const canManageUser = (empId) => {
        if (user?.role === 'admin') return true;
        if (myPermissions && myPermissions.role === 'reader') return false;
        if (isAdminOrManager) return true;
        if (myPermissions && myPermissions.role === 'team_lead') {
            const mu = myPermissions.managed_users?.find(u => u.id === empId);
            return mu ? mu.can_edit === true : false;
        }
        return false;
    };

    const startEdit = (entry) => {
        setIsCreatingForUser(false);
        setEditingId(entry.id);

        let loadedTasks = [];
        try {
            loadedTasks = typeof entry.tasks === 'string' ? JSON.parse(entry.tasks) : entry.tasks;
        } catch (e) { }

        if (!loadedTasks || !Array.isArray(loadedTasks) || loadedTasks.length === 0) {
            loadedTasks = [{ description: entry.activity || '', hours: entry.hours || 0 }];
        }

        setInitialFormData({
            date: getLocalDate(entry.entry_date),
            project_id: entry.project_id || null,
            tasks: loadedTasks,
            notes: entry.notes || ''
        });
    };

    const startCreate = () => {
        setEditingId(null);
        setIsCreatingForUser(true);
        setInitialFormData({
            date: new Date().toISOString().split('T')[0],
            project_id: null,
            tasks: [{ description: '', hours: '' }],
            notes: ''
        });
    };

    const handleFormCancel = () => {
        setEditingId(null);
        setIsCreatingForUser(false);
    };

    const handleFormSubmit = async (formData, isUpdate) => {
        setLoading(true);
        try {
            const payload = {
                entry_date: formData.date,
                project_id: formData.project_id,
                tasks: formData.tasks,
                notes: formData.notes,
                user_id: selectedEmp.id // WICHTIG: Damit Server weiß, für wen
            };

            if (isUpdate && editingId) {
                await adminApi.editTimesheetEntry(editingId, payload);
            } else {
                // CREATE ON BEHALF
                await adminApi.createTimesheetEntryForUser(payload);
            }
            
            // Reload to get properly joined project data
            fetchEmpEntries(selectedEmp.id);

            setEditingId(null);
            setIsCreatingForUser(false);
        } catch (err) {
            console.error(err);
            if (err.response?.status === 409 && err.response?.data?.existingEntry) {
                setExistingEntryInfo(err.response.data.existingEntry);
                setShowExistsModal(true);
            } else {
                alert(err.response?.data?.error || "Fehler beim Speichern");
            }
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        try {
            await adminApi.updateTimesheetSettings(settings);
            alert("Einstellungen gespeichert!");
        } catch (err) { alert("Fehler beim Speichern"); }
    };

    const renderCard = (emp) => {
        const countDel = Number(emp.pending_deletions) || 0;
        const countApp = Number(emp.pending_approvals) || 0;
        const isClean = countDel === 0 && countApp === 0;

        return (
            <div key={emp.id}
                className={`employee-card ${countDel > 0 ? 'card-deletion' : (countApp > 0 ? 'card-pending' : 'card-neutral')}`}
                onClick={() => { setSelectedEmp(emp); setView('detail'); }}
            >
                <div className="card-header">
                    <span className="emp-name">{emp.name}</span>
                    <div className="status-icons">
                        {countDel > 0 && <span>🗑️</span>}
                        {countApp > 0 && <span>⏱️</span>}
                        {isClean && <span style={{ opacity: 0.5, filter: 'grayscale(1)' }}>✅</span>}
                    </div>
                </div>
                <div className="card-body">
                    {countDel > 0 && <div className="status-row del"><strong>{countDel}</strong> Löschanträge</div>}
                    {countApp > 0 && <div className="status-row pen"><strong>{countApp}</strong> zu prüfen</div>}
                    {isClean && <div className="status-row ok" style={{ width: '100%', textAlign: 'center' }}>✓ Keine offenen Aufgaben</div>}
                </div>
            </div>
        );
    };

    // --- Filter & Gruppen Logik ---
    const filteredEmployees = employees.filter(e =>
        e.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    let finalEmployees = filteredEmployees;
    if (!isAdminOrManager && myPermissions && myPermissions.role === 'team_lead') {
        const managedIds = (myPermissions.managed_users || []).map(mu => mu.id);
        finalEmployees = filteredEmployees.filter(e => managedIds.includes(e.id));
    }

    const actionRequired = finalEmployees.filter(e =>
        Number(e.pending_deletions) > 0 || Number(e.pending_approvals) > 0
    );

    const cleanEmployees = finalEmployees.filter(e =>
        Number(e.pending_deletions) === 0 && Number(e.pending_approvals) === 0
    );

    const admins = cleanEmployees.filter(e => e.role && e.role.toLowerCase() === 'admin');
    const staff = cleanEmployees.filter(e => !e.role || e.role.toLowerCase() !== 'admin');

    return (
        <div className="admin-detail-view" style={isEmbedded ? { border: 'none', padding: 0 } : {}}>
            {!isEmbedded && (
                <div className={`header-container ${view === 'detail' ? 'compact' : ''}`}>
                    <div className="header-top-row">
                        <h2 className="page-main-title">Stundennachweis Verwaltung</h2>
                        <button onClick={() => { if (onBack) onBack(); }} className="btn-header-back">
                            ← Zurück zur Tool-Übersicht
                        </button>
                    </div>
                    {(mainTab === 'settings' || view === 'list') && (
                        <div className="admin-nav-tabs">
                            <button
                                className={`nav-tab-btn ${mainTab === 'management' ? 'active' : ''}`}
                                onClick={() => { setMainTab('management'); setView('list'); setSelectedEmp(null); fetchEmployees(); }}
                            >
                                👥 Verwaltung & Einträge
                            </button>
                            <button
                                className={`nav-tab-btn ${mainTab === 'settings' ? 'active' : ''}`}
                                onClick={() => setMainTab('settings')}
                            >
                                ⚙️ Einstellungen
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* TAB: SETTINGS */}
            {mainTab === 'settings' && (
                <div className="settings-container">
                    <form onSubmit={saveSettings}>
                        <div className="settings-card">
                            <h4 className="settings-group-title">✅ Genehmigungsregel</h4>
                            <div className="radio-options-list">
                                <label className="radio-option-item">
                                    <input type="radio" name="approval_mode" value="always" checked={settings.approval_mode === 'always'} onChange={e => setSettings({ ...settings, approval_mode: e.target.value })} />
                                    <div className="radio-label-content"><span className="radio-label-title">Immer Genehmigung erforderlich</span><span className="radio-label-desc">Jeder Eintrag und jede Änderung muss vom Admin genehmigt werden.</span></div>
                                </label>
                                <label className="radio-option-item">
                                    <input type="radio" name="approval_mode" value="changes_only" checked={settings.approval_mode === 'changes_only'} onChange={e => setSettings({ ...settings, approval_mode: e.target.value })} />
                                    <div className="radio-label-content"><span className="radio-label-title">Nur Änderungen erfordern Bestätigung</span><span className="radio-label-desc">Ersteinträge sind automatisch genehmigt. Nur nachträgliche Änderungen erfordern eine Genehmigung.</span></div>
                                </label>
                                <label className="radio-option-item">
                                    <input type="radio" name="approval_mode" value="never" checked={settings.approval_mode === 'never'} onChange={e => setSettings({ ...settings, approval_mode: e.target.value })} />
                                    <div className="radio-label-content"><span className="radio-label-title">Keine Genehmigung erforderlich</span><span className="radio-label-desc">Alle eingereichten Einträge werden sofort genehmigt</span></div>
                                </label>
                            </div>
                        </div>
                        <div className="settings-card">
                            <h4 className="settings-group-title">✏️ Bearbeitungszeitraum</h4>
                            <div style={{ padding: '5px 0' }}>
                                <label className="unlimited-toggle">
                                    <input type="checkbox" checked={Number(settings.allow_edit_past_days) < 0}
                                        onChange={(e) => {
                                            if (e.target.checked) setSettings({ ...settings, allow_edit_past_days: -1 });
                                            else setSettings({ ...settings, allow_edit_past_days: 30 });
                                        }}
                                    />
                                    Unbegrenzt in die Vergangenheit erlauben
                                </label>
                                <div className="input-with-unit" style={{ opacity: Number(settings.allow_edit_past_days) < 0 ? 0.5 : 1 }}>
                                    <input type="number" min="0" disabled={Number(settings.allow_edit_past_days) < 0}
                                        value={Number(settings.allow_edit_past_days) < 0 ? '' : settings.allow_edit_past_days}
                                        onChange={e => setSettings({ ...settings, allow_edit_past_days: e.target.value })}
                                    />
                                    <span style={{ color: '#6b7280' }}>Tage</span>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button type="submit" className="btn-save-settings">Einstellungen speichern</button></div>
                    </form>
                </div>
            )}

            {/* TAB: MANAGEMENT */}
            {mainTab === 'management' && (
                <>
                    {/* LIST VIEW */}
                    {view === 'list' && (
                        <div className="admin-employee-list">
                            <div style={{ marginBottom: '20px', position: 'relative' }}>
                                <input
                                    type="text"
                                    placeholder="🔍 Mitarbeiter suchen..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        width: '100%', padding: '12px 15px', borderRadius: '8px',
                                        border: '1px solid #ddd', fontSize: '1rem', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                                    }}
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        style={{
                                            position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                                            background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '1.2rem'
                                        }}
                                    >✕</button>
                                )}
                            </div>

                            {loading ? <p>Lade Mitarbeiter...</p> : (
                                <>
                                    {actionRequired.length > 0 && (
                                        <div className="section-block urgent-block" style={{
                                            border: '2px solid #fecaca', background: '#fff1f2',
                                            borderRadius: '8px', padding: '15px', marginBottom: '30px'
                                        }}>
                                            <h4 className="section-title" style={{ color: '#b91c1c', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                🔥 Handlungsbedarf ({actionRequired.length})
                                            </h4>
                                            <p style={{ fontSize: '0.9rem', color: '#991b1b', marginBottom: '15px' }}>
                                                Bei folgenden Personen stehen Löschungen oder Genehmigungen aus.
                                            </p>
                                            <div className="employee-grid">
                                                {actionRequired.map(renderCard)}
                                            </div>
                                        </div>
                                    )}

                                    {admins.length > 0 && (
                                        <div className="section-block admin-block">
                                            <h4 className="section-title">🛡️ Administrator</h4>
                                            <div className="employee-grid">{admins.map(renderCard)}</div>
                                        </div>
                                    )}

                                    <div className="section-block staff-block">
                                        <h4 className="section-title">👷 Mitarbeiter</h4>
                                        {staff.length > 0 ? (
                                            <div className="employee-grid">{staff.map(renderCard)}</div>
                                        ) : (
                                            <p style={{ color: '#888', fontStyle: 'italic', padding: '10px' }}>Keine Mitarbeiter gefunden.</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* DETAIL VIEW */}
                    {view === 'detail' && selectedEmp && (
                        <div className="detail-view-container" style={isEmbedded ? { padding: '20px' } : {}}>
                            <div className="detail-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', gap: '15px', flexWrap: 'wrap' }}>
                                <div className="detail-person-info" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <h3 className="detail-person-name" style={{ margin: 0 }}>{selectedEmp.name}</h3>
                                    <span className={`detail-person-role ${selectedEmp.role === 'admin' ? 'is-admin' : ''}`} style={{ margin: 0, whiteSpace: 'nowrap' }}>
                                        {selectedEmp.role === 'admin' ? 'Administrator' : 'Mitarbeiter'}
                                    </span>
                                </div>
                                <button onClick={() => { setView('list'); setSelectedEmp(null); fetchEmployees(); setIsCreatingForUser(false); }} className="btn-list-back" style={{ flexShrink: 0 }}>
                                    ← Zurück
                                </button>
                            </div>

                            {/* CREATE ENTRY BOX OVER FILTERS - Styled exactly like "Mein Nachweis" */}
                            {canManageUser(selectedEmp.id) && (
                                <div className={`entry-form-card ${isCreatingForUser ? 'expanded' : 'collapsed'}`} style={{ marginBottom: isCreatingForUser ? '30px' : '20px' }}>
                                    <div
                                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                                        onClick={() => {
                                            if (!isCreatingForUser) {
                                                startCreate();
                                            } else {
                                                setIsCreatingForUser(false);
                                            }
                                        }}
                                    >
                                        <h2 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            ✨ Neuer Tagesbericht
                                        </h2>
                                        <span style={{ color: '#059669', fontSize: '1.2rem', transition: 'transform 0.3s', transform: isCreatingForUser ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                                    </div>

                                    {isCreatingForUser && (
                                        <TimesheetForm
                                            initialData={initialFormData}
                                            onSubmit={handleFormSubmit}
                                            onCancel={handleFormCancel}
                                            loading={loading}
                                            entryId={null}
                                        />
                                    )}
                                </div>
                            )}

                            <div className="admin-filters" style={{ flexWrap: 'wrap', gap: '15px', marginTop: 0 }}>
                                <div className="filter-group">
                                    <label>Von:</label>
                                    <input type="date" value={filters.start} onChange={e => setFilters({ ...filters, start: e.target.value })} />
                                </div>
                                <div className="filter-group">
                                    <label>Bis:</label>
                                    <input type="date" value={filters.end} onChange={e => setFilters({ ...filters, end: e.target.value })} />
                                </div>

                                <div className="filter-group">
                                    <div className="filter-dropdown-container" ref={statusDropdownRef}>
                                        <button
                                            className="filter-dropdown-btn"
                                            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                        >
                                            <span>{getStatusButtonText()}</span>
                                            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>▼</span>
                                        </button>
                                        {showStatusDropdown && (
                                            <div className="filter-dropdown-menu">
                                                <div className="dropdown-options">
                                                    <label className="dropdown-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.statuses.length === 3}
                                                            onChange={toggleAllStatuses}
                                                        />
                                                        <span>Alle</span>
                                                    </label>
                                                    <div className="dropdown-divider"></div>
                                                    <label className="dropdown-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.statuses.includes('eingereicht')}
                                                            onChange={() => toggleStatus('eingereicht')}
                                                        />
                                                        <span>Eingereicht</span>
                                                    </label>
                                                    <label className="dropdown-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.statuses.includes('überarbeitung_angefordert')}
                                                            onChange={() => toggleStatus('überarbeitung_angefordert')}
                                                        />
                                                        <span>Korrektur angefordert</span>
                                                    </label>
                                                    <label className="dropdown-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.statuses.includes('genehmigt')}
                                                            onChange={() => toggleStatus('genehmigt')}
                                                        />
                                                        <span>Genehmigt</span>
                                                    </label>
                                                </div>
                                                <div className="dropdown-actions">
                                                    <button className="btn-dropdown-none" onClick={() => setFilters(prev => ({ ...prev, statuses: [] }))}>Keine</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <select
                                        className="filter-dropdown-btn"
                                        value={filters.sortOrder}
                                        onChange={(e) => setFilters({ ...filters, sortOrder: e.target.value })}
                                        style={{ width: '130px' }}
                                    >
                                        <option value="desc">Neu → Alt</option>
                                        <option value="asc">Alt → Neu</option>
                                    </select>
                                </div>

                                <div className="filter-group" style={{ marginLeft: '5px' }}>
                                    <button
                                        onClick={resetFilters}
                                        disabled={!isFilterActive}
                                        className="btn-reset-icon"
                                        title="Filter zurücksetzen"
                                    >
                                        ↺
                                    </button>
                                </div>

                                <div className="filter-export-section" style={{ marginLeft: 'auto' }}>
                                    <button
                                        onClick={() => setIsExportOpen(true)}
                                        className="btn-header-secondary"
                                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', fontSize: '0.85rem', height: '38px' }}
                                    >
                                        <span style={{ fontSize: '1.1rem' }}>📄</span> PDF Export
                                    </button>
                                </div>
                            </div>

                            <div className="entries-list-admin" style={{ marginTop: '20px' }}>
                                {loading && !isCreatingForUser && <p style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#666' }}>Lade Einträge...</p>}
                                {!loading && entries.length === 0 && !isCreatingForUser && <p style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#9ca3af', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>Keine Einträge für diesen Zeitraum/Status gefunden.</p>}
                                {!loading && entries.length > 0 && (() => {
                                    const sortedEntries = [...entries].sort((a, b) => {
                                        const dateA = new Date(a.entry_date);
                                        const dateB = new Date(b.entry_date);
                                        return filters.sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
                                    });

                                    const grouped = {};
                                    sortedEntries.forEach(e => {
                                        const d = getLocalDate(e.entry_date);
                                        if (!grouped[d]) grouped[d] = [];
                                        grouped[d].push(e);
                                    });

                                    return Object.keys(grouped).sort((a, b) => {
                                        return filters.sortOrder === 'desc' ? new Date(b) - new Date(a) : new Date(a) - new Date(b);
                                    }).map(date => {
                                        const entry = grouped[date][0];
                                        const isExpanded = expandedDates[date];
                                        const [yyyy, mm, dd] = date.split('-');
                                        const displayDate = `${dd}.${mm}.${yyyy}`;
                                        const headerStatus = getEntryHeaderStatus(entry);

                                        return (
                                            <div key={date}
                                                className={`day-section-wrapper ${headerStatus.bg === '#fef2f2' ? 'deletion' : (headerStatus.bg === '#ffedd5' ? 'pending' : (headerStatus.bg === '#ecfdf5' ? 'approved' : 'neutral'))}`}
                                                style={{ borderLeft: `5px solid ${headerStatus.color}` }}
                                            >
                                                <div className={`day-header ${isExpanded ? 'expanded' : ''}`} onClick={() => setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }))}>
                                                    <div className="header-left"><span>{isExpanded ? '▼' : '▶'}</span> {displayDate}<span className="header-status-badge" style={{ color: headerStatus.color, background: headerStatus.bg }}>{headerStatus.text}</span></div>
                                                    <div className="header-hours">{entry.hours} Std.</div>
                                                </div>

                                                {isExpanded && (
                                                    <div className="day-content">
                                                        {editingId === entry.id ? (
                                                            <div className="admin-edit-box">
                                                                <TimesheetForm
                                                                    initialData={initialFormData}
                                                                    onSubmit={handleFormSubmit}
                                                                    onCancel={handleFormCancel}
                                                                    loading={loading}
                                                                    entryId={entry.id}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div>
                                                                    {entry.project_name && <div style={{ marginBottom: '10px', color: '#4f46e5', fontWeight: 600 }}>📁 {entry.project_name}</div>}
                                                                    {(() => {
                                                                        let tasks = [];
                                                                        try { tasks = typeof entry.tasks === 'string' ? JSON.parse(entry.tasks) : entry.tasks; } catch (e) { }
                                                                        if (!tasks || !tasks.length) tasks = [{ description: entry.activity, hours: entry.hours }];
                                                                        return tasks.map((t, idx) => (<div key={idx} className="task-row"><span>{t.description}</span><span className="task-time">{t.hours}h</span></div>));
                                                                    })()}
                                                                    {entry.notes && <div style={{ marginTop: '10px', color: '#666', fontStyle: 'italic', fontSize: '0.9rem' }}>📝 {entry.notes}</div>}
                                                                </div>
                                                                {canManageUser(selectedEmp.id) && (
                                                                    <div className="admin-card-actions">
                                                                        {entry.deletion_request_pending && <><button onClick={() => handleDelete(entry.id)} className="btn-admin-action btn-delete-confirm">🗑️ Bestätigen</button><button onClick={() => handleRejectDeletion(entry.id)} className="btn-admin-action btn-reject">❌ Ablehnen</button></>}
                                                                        {!entry.deletion_request_pending && entry.status === 'eingereicht' && <><button onClick={() => handleApprove(entry.id)} className="btn-admin-action btn-approve">✅ Genehmigen</button><button onClick={() => handleRejectEntry(entry.id)} className="btn-admin-action btn-reject">↩️ Ablehnen</button></>}
                                                                        {(!entry.deletion_request_pending) && <button onClick={() => startEdit(entry)} className="btn-admin-action btn-edit">✏️ Bearbeiten</button>}
                                                                        {!entry.deletion_request_pending && <button onClick={() => handleDelete(entry.id)} className="btn-admin-action btn-delete">🗑️ Löschen</button>}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    )}
                </>
            )}

            <TimesheetExportModal
                isOpen={isExportOpen}
                onClose={() => setIsExportOpen(false)}
                entries={entries}
                user={selectedEmp || user}
                title="Stundennachweis"
                isAdminView={true}
            />

            {showExistsModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px' }}>
                        <h3>⚠️ Eintrag existiert bereits</h3>
                        <p>Für den <strong>{new Date(existingEntryInfo?.entry_date).toLocaleDateString()}</strong> liegt bereits ein Eintrag vor.</p>
                        <p>Status: <strong>{existingEntryInfo?.status?.toUpperCase()}</strong></p>
                        <div className="modal-actions">
                            <button onClick={() => { setShowExistsModal(false); }} className="btn-cancel">Abbrechen</button>
                            <button onClick={() => {
                                setShowExistsModal(false);
                                const inList = entries.some(e => e.id === existingEntryInfo.id);
                                if (!inList) {
                                    setEntries(prev => [...prev, existingEntryInfo].sort((a, b) => new Date(b.entry_date).getTime() - new Date(a.entry_date).getTime()).reverse());
                                }
                                const d = getLocalDate(existingEntryInfo.entry_date);
                                setExpandedDates(prev => ({ ...prev, [d]: true }));
                                setTimeout(() => startEdit(existingEntryInfo), 0);
                            }} className="btn-primary">Diesen Eintrag bearbeiten</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}